import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  updateVenuePropState,
  updateVenueState,
} from '../../features/venue/venueSlice';
import { updateVenue } from '../../lib/apis/venue';
import {
  getServiceProviderState,
  updateServiceProviderState,
} from '../../features/serviceProvider/serviceProviderSlice';
import { updateServiceProvider } from '../../lib/apis/serviceProvider';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Offset from './Offset';
import EditForm from './EditForm';
import Input from '../atoms/Input';
import Property from '../atoms/Property';
import { clearObj } from '../../lib/utils';
import {
  contact_details_fields,
  social_media_fields,
} from '../../data/display';
import styles from '../../styles/components/organisms/ItemInfo.module.scss';

interface ItemInfoFormProps {
  venueID?: string;
  serviceProviderID?: string;
  prefill?: any;
  onSubmit?: (id?: string) => void;
  onCancel?: () => void;
}

const ItemInfoForm = (props: ItemInfoFormProps) => {
  const { venueID, serviceProviderID, prefill, onSubmit, onCancel } = props;
  const { headline, description } = prefill;
  const itemType = !!venueID ? 'venue' : 'service';
  const { t } = useTranslation(['common', 'venue', 'service']);
  const dispatch = useAppDispatch();
  const itemID = venueID || serviceProviderID;
  const updateItemState = !!venueID
    ? updateVenueState
    : updateServiceProviderState;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState() as any;
  const [fieldErrors, setFieldErrors] = useState([]) as any;
  const formID = 'ItemInfoForm';

  const contactDetailsFields = Object.keys(contact_details_fields);
  const socialMediaFields = Object.keys(social_media_fields);
  const contactFields = [...contactDetailsFields, ...socialMediaFields];

  /**
   * Handle errors
   */
  const handleError = (error: any, payload?: any) => {
    console.error(formID, error, payload);
    setError(error);
    setLoading(false);
  };

  /**
   * Handle form submit
   */
  const submit = (formData: any) => {
    const {
      email,
      phone,
      website,
      facebook,
      instagram,
      twitter,
      youtube,
      linkedin,
    } = formData;

    const payload = clearObj({
      ...formData,
      id: null,
      type: null,
      contact_details: clearObj({
        venue_id: venueID,
        service_provider_id: serviceProviderID,
        email,
        phone,
        website,
        facebook,
        instagram,
        twitter,
        youtube,
        linkedin,
      }),
    });

    contactFields.forEach((el) => delete payload[el]);

    setError(false);
    setLoading(true);
    if (!!itemID) {
      try {
        // Save to database and update venueState
        const updateAPI = venueID ? updateVenue : updateServiceProvider;
        updateAPI(itemID, payload).then((item) => {
          console.log('updateItem', payload, item);
          setLoading(false);
          /**
           * TODO: return contact_details in updateVenue response
           */
          dispatch(
            updateItemState({
              ...item,
              contact_details: payload.contact_details,
            })
          );
          if (!!onSubmit) {
            onSubmit(item);
          }
        });
      } catch (error) {
        handleError(error, payload);
      }
    }
  };

  const cancel = () => {
    if (!!onCancel) {
      onCancel();
    }
  };

  return (
    <>
      <EditForm
        id={formID}
        error={error}
        loading={loading}
        onSubmit={submit}
        onCancel={cancel}
        onFieldError={(arr: any) => setFieldErrors(arr)}
      >
        <Row className={styles.itemInfo}>
          <Col xs={8} md={4}>
            <br />
            <Property dark icon="description" value={t(`common:description`)} />
            <Input
              optional
              reset
              name="headline"
              icon="description"
              label={t(`${itemType}:inputHeadline`)}
              defaultValue={headline}
            />
            <Input
              reset
              className={styles.textarea}
              format="textarea"
              name="description"
              icon="description"
              label={t(`${itemType}:inputDescription`)}
              defaultValue={description}
              error={fieldErrors.indexOf('description') > -1}
            />
          </Col>
          <Col xs={8} md={4}>
            <br />
            <Property dark icon="contact" label={t('common:contact')} />
            <Offset>
              {contactFields.map((el, i) => (
                <Input
                  reset
                  half
                  key={i}
                  optional={socialMediaFields.indexOf(el) > -1}
                  name={el}
                  format={el}
                  icon={el}
                  label={t(`common:${el}`)}
                  defaultValue={prefill[el]}
                  placeholder={
                    el === 'phone'
                      ? t(`common:input-phone-exp`, {
                          countryCode: '49',
                        })
                      : ''
                  }
                  unit={el === 'phone' ? t(`common:input-phone-help`) : ''}
                  error={fieldErrors.indexOf(el) > -1}
                />
              ))}
            </Offset>
          </Col>
        </Row>
      </EditForm>
    </>
  );
};

export default ItemInfoForm;
